import React, { createContext, useCallback, useMemo } from "react";
import { useFetchGet } from "hooks/fetch/useFetchGet";
import buildUserName from "core/utils/build_user_name";
import { UNKNOWN } from "core/constants";
import { User, UserType } from "interfaces/user";

export interface UserContextInterface {
  loading: boolean;
  error: boolean;
  users: User[];
  internalUsers: User[];
  nonServiceAccountUsers: User[];
  getFullName: (id: number) => string;
}
export const UsersContext = createContext<UserContextInterface>({
  loading: true,
  error: false,
  users: [],
  nonServiceAccountUsers: [],
  internalUsers: [],
  getFullName: _id => UNKNOWN
});
UsersContext.displayName = "UsersContext";

export const UsersProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const {
    loading,
    error,
    response: { results: users } = { results: [] }
  } = useFetchGet("users?ordering=first_name,last_name&size=9999&system=1");

  const getFullName = useCallback(
    (userId: number) => {
      const user = users.find((user: User) => user.id === userId);
      return buildUserName(user);
    },
    [users]
  );

  const nonServiceAccountUsers = useMemo(() => {
    return users?.filter(
      (user: User) => !user.is_service_account && user.is_active
    );
  }, [users]);

  const internalUsers = useMemo(() => {
    return users?.filter(
      (user: User) => user.user_type === UserType.INTERNAL && user.is_active
    );
  }, [users]);

  return (
    <UsersContext.Provider
      value={{
        loading,
        error,
        users,
        nonServiceAccountUsers,
        internalUsers,
        getFullName
      }}
    >
      {children}
    </UsersContext.Provider>
  );
};
